// Onboarding conversacional — el agente reemplaza el FORMULARIO de perfil, pero
// conserva el mismo contrato de datos (los campos que guarda Perfil.jsx). El
// agente conversa, va capturando los campos y, cuando el perfil está completo,
// la UI lo guarda en `profiles` con perfil_completado=true (idéntico a Perfil).
//
// Diseño seguro: el modelo SUGIERE campos; la validación es autoritativa del
// lado cliente (perfilListo / fusionarPerfil contra enums cerrados). Nunca se
// guarda algo fuera de vocabulario ni a medias.
//
// Modelo: capa 'onboarding_agente' → Haiku por el ruteo default (conversación
// liviana). Flag de uso lo decide App; este módulo es plataforma-agnóstico.
//
// NOTA (2026-08-02): el componente OnboardingAgente pasó a un GUION
// determinista (preguntas con respuestas tocables, cero IA) porque el chat
// libre perdía gente; de este módulo hoy solo consume perfilListo y
// camposCapturados. El motor conversacional (conversarOnboarding,
// fusionarPerfil, parseRespuesta y la capa 'onboarding_agente') queda
// recuperable sin cablear, con sus tests vivos.

import { callClaude } from './ai';
import { normalizarInstitucion } from '../data/instituciones';
import { UNIVERSIDADES_VALIDAS, SERVICIOS_VALIDOS } from './perfilCatalogos';
import { ROLES_VALIDOS } from './roles';

// Vocabulario cerrado. Los roles salen de lib/roles.js (fuente única desde el
// 2026-10-04); se re-exportan para no mover a los consumidores de siempre.
export { ROLES_VALIDOS };
export const ETAPAS_VALIDAS = ['practica_pregrado', 'pre_internado', 'internado_en_curso', 'post_internado'];
// Espejo de ORIGENES en OnboardingAgente.jsx / ORIGEN_OPTIONS en Perfil.jsx.
// 'sin_respuesta' NO está aquí a propósito: es un valor del guion (la burbuja
// necesita algo que mostrar) que a la BD viaja como no haber respondido.
export const ORIGENES_VALIDOS = ['feneech', 'companero', 'instagram', 'universidad', 'congreso', 'otro'];

const texto = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * Traduce UNA respuesta del guion a la fila parcial que se persiste mientras el
 * usuario avanza. Devuelve null si el valor no debe viajar a la BD.
 *
 * Por qué existe: hasta el 6-ago-2026 el onboarding no escribía NADA hasta el
 * botón final, así que quien respondía 3 de 5 preguntas y cerraba la pestaña
 * perdía todo y volvía a empezar de cero. En la auditoría de ese día, 74 de 97
 * cuentas abandonadas nunca completaron el perfil.
 *
 * NUNCA marca perfil_completado: eso lo decide el guardado final, que sigue
 * siendo el autoritativo. Solo emite valores de catálogo cerrado (el texto
 * libre se limita a nombre e institución, igual que el guardado final).
 */
export function filaParcial(campo, valor) {
  if (campo === 'nombre') {
    const v = texto(valor);
    return v ? { nombre: v } : null;
  }
  if (campo === 'rol') {
    return ROLES_VALIDOS.includes(valor) ? { rol: valor } : null;
  }
  if (campo === 'etapa_internado') {
    return ETAPAS_VALIDAS.includes(valor) ? { etapa_internado: valor } : null;
  }
  if (campo === 'anio_carrera') {
    const n = Number(valor);
    return Number.isFinite(n) && n >= 1 && n <= 7 ? { anio_carrera: n } : null;
  }
  if (campo === 'institucion') {
    const v = texto(valor);
    const norm = v ? normalizarInstitucion(v) : null;
    return norm ? { institucion: norm } : null;
  }
  if (campo === 'origen_conocido') {
    return ORIGENES_VALIDOS.includes(valor) ? { origen_conocido: valor } : null;
  }
  // Perfil enriquecido opt-in (catálogos cerrados en perfilCatalogos.js): un
  // valor fuera de catálogo no viaja, igual que el origen.
  if (campo === 'universidad') {
    return UNIVERSIDADES_VALIDAS.includes(valor) ? { universidad: valor } : null;
  }
  if (campo === 'servicio') {
    return SERVICIOS_VALIDOS.includes(valor) ? { servicio: valor } : null;
  }
  return null;
}

/**
 * Toma la fila persistida y devuelve SOLO los campos que el guion puede dar por
 * respondidos, para reanudar en el paso que falta en vez de partir de cero.
 *
 * Un rol fuera de vocabulario NO cuenta como respondido. Importa: el DEFAULT
 * viejo de la columna dejaba rol='interno' en toda cuenta que abandonaba, y ese
 * valor no está en ROLES_VALIDOS. Si se diera por bueno, pasosPara() saltaría
 * la pregunta de rol y perfilListo() jamás devolvería true: el usuario
 * respondería todo y nunca vería el botón para entrar. Se vuelve a preguntar,
 * que es lo correcto porque nunca lo respondió.
 */
export function perfilParaReanudar(profile) {
  if (!profile || typeof profile !== 'object') return {};
  const out = {};
  const campos = ['nombre', 'rol', 'etapa_internado', 'anio_carrera', 'institucion', 'origen_conocido', 'universidad', 'servicio'];
  for (const campo of campos) {
    const fila = filaParcial(campo, profile[campo]);
    if (fila) Object.assign(out, fila);
  }
  return out;
}

const SYSTEM = `Eres Benito, el agente de bienvenida de MiPAE, una app de acompañamiento clínico para enfermería en Chile. Recibes a alguien que recién entra y, conversando de forma cálida y breve, completas su perfil.

OBJETIVO: capturar estos datos, preguntando UNA cosa a la vez (no un formulario de golpe):
- nombre (cómo le gusta que le digan)
- rol: uno de [estudiante, enfermero, tens] (docencia y postgrado son enfermero)
- institucion: su universidad/hospital/centro
- etapa_internado (SOLO si rol = "estudiante"): uno de [practica_pregrado, pre_internado, internado_en_curso, post_internado]
- anio_carrera (SOLO si rol = "estudiante"): número de año de carrera (1 a 7)

REGLAS:
- Español de Chile, cercano y MUY breve: una o dos frases, una sola pregunta por turno.
- Mapea lo que diga a los valores válidos (ej.: "soy interno" → rol estudiante + etapa internado_en_curso; "trabajo en la UCI del Sótero" → rol enfermero + institucion "Hospital Sótero del Río"; "soy docente" → rol enfermero). No inventes datos que no haya dicho.
- No pidas datos que ya tienes.
- Cuando tengas todo lo requerido para su rol, pon "completo": true y cierra con una frase cálida de bienvenida (sin más preguntas).
- Nunca pidas RUT, contraseñas ni datos de pacientes.

Responde EXCLUSIVAMENTE con un objeto JSON válido, sin texto antes ni después:
{"mensaje":"lo que le dices","perfil":{"nombre":"...","rol":"...","institucion":"...","etapa_internado":"...","anio_carrera":0},"completo":false}
Incluye en "perfil" SOLO los campos de los que estés seguro en este punto de la conversación; omite el resto.`;

function extraerTexto(content) {
  if (!Array.isArray(content)) return '';
  return content.filter((b) => b?.type === 'text').map((b) => b.text || '').join('');
}

// Funde los campos que sugiere el modelo sobre el perfil acumulado, coaccionando
// a los enums/tipos válidos. Lo que no calza se descarta (no se pisa lo previo).
export function fusionarPerfil(prev, ia) {
  const out = { ...(prev || {}) };
  if (ia && typeof ia === 'object') {
    if (typeof ia.nombre === 'string' && ia.nombre.trim()) out.nombre = ia.nombre.trim();
    if (ROLES_VALIDOS.includes(ia.rol)) out.rol = ia.rol;
    if (typeof ia.institucion === 'string' && ia.institucion.trim()) out.institucion = ia.institucion.trim();
    if (ETAPAS_VALIDAS.includes(ia.etapa_internado)) out.etapa_internado = ia.etapa_internado;
    const anio = Number(ia.anio_carrera);
    if (Number.isFinite(anio) && anio >= 1 && anio <= 7) out.anio_carrera = anio;
  }
  return out;
}

// Campos del perfil que el onboarding captura, en el orden de la conversación.
export const CAMPOS_PERFIL = ['nombre', 'rol', 'institucion', 'etapa_internado', 'anio_carrera'];

// Nombres de los campos que `despues` tiene y `antes` no. Devuelve solo NOMBRES
// de campo (nunca valores): alimenta la instrumentación beat a beat del
// onboarding sin arrastrar PII a eventos_v2 — la auditoría de jul-2026 mostró
// que la mayoría abandona sin que sepamos en qué campo, y esto lo hace visible.
export function camposCapturados(antes, despues) {
  const lleno = (v) => v !== undefined && v !== null && String(v).trim() !== '';
  return CAMPOS_PERFIL.filter((c) => !lleno(antes?.[c]) && lleno(despues?.[c]));
}

// Validación autoritativa (espejo de puedeGuardarFn en Perfil.jsx). No confiamos
// en el "completo" del modelo: esto manda.
// La institución (texto libre) ya no se le pide al estudiante (A17,
// 2026-10-04): su universidad es la lista opcional del perfil enriquecido
// (aplicaInstitucion en perfilCatalogos.js). Enfermero/a y TENS la siguen
// dando, y un rol fuera de vocabulario la sigue exigiendo, como siempre.
export function perfilListo(p) {
  if (!p?.nombre?.trim() || !p?.rol) return false;
  if (p.rol !== 'estudiante' && !p?.institucion?.trim()) return false;
  if (p.rol === 'estudiante' && (!p.etapa_internado || !p.anio_carrera)) return false;
  return true;
}

// Extrae el JSON de la respuesta (tolerante a cercas ``` y texto alrededor).
// Devuelve { mensaje, perfil } o null. `perfil` es lo CRUDO del modelo (se funde
// con fusionarPerfil aguas arriba).
export function parseRespuesta(texto) {
  if (!texto || typeof texto !== 'string') return null;
  let t = texto.trim();
  const fence = t.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) t = fence[1].trim();
  if (!t.startsWith('{')) {
    const a = t.indexOf('{');
    const b = t.lastIndexOf('}');
    if (a >= 0 && b > a) t = t.slice(a, b + 1);
  }
  let obj = null;
  try { obj = JSON.parse(t); } catch { obj = null; }
  if (!obj || typeof obj !== 'object') {
    const plano = texto.trim();
    return plano ? { mensaje: plano, perfil: null } : null;
  }
  const mensaje = typeof obj.mensaje === 'string' ? obj.mensaje.trim() : '';
  if (!mensaje) return null;
  return { mensaje, perfil: obj.perfil && typeof obj.perfil === 'object' ? obj.perfil : null };
}

/**
 * Un turno del onboarding conversacional.
 *  - historial: turnos previos del chat ([{role:'user'|'assistant', content}]).
 *  - perfilActual: lo capturado hasta ahora (para que el modelo no re-pregunte).
 * Devuelve { mensaje, perfil } donde `perfil` ya viene fundido/validado.
 * Lanza Error presentable si falla.
 */
export async function conversarOnboarding({ historial = [], perfilActual = {} }, signal) {
  const messages = [
    { role: 'user', content: `[Datos capturados hasta ahora]\n${JSON.stringify(perfilActual)}` },
    { role: 'assistant', content: 'Listo, sigo desde ahí.' },
    ...historial,
  ];
  const body = { max_tokens: 500, system: SYSTEM, messages };
  const res = await callClaude(body, signal, { capa: 'onboarding_agente' });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error?.message || data?.error || 'No pude seguir ahora. Intenta de nuevo.');
  }
  const out = parseRespuesta(extraerTexto(data?.content));
  if (!out) throw new Error('No pude seguir ahora. Intenta de nuevo.');
  return { mensaje: out.mensaje, perfil: fusionarPerfil(perfilActual, out.perfil) };
}
